import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Easing, StyleSheet, type TextStyle } from 'react-native';
import Animated, { useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import { A } from '@expo/html-elements';
import { theme } from '@garnish/constants';

import { BodyText, webOnlyStyles } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useWebClassName } from '@order/shared-hooks';

import { USABLE_NET_BANNER_HEIGHT } from '../constants';
import { useUsableNet } from '../useUsableNet';
import { useUsableNetScrolling } from '../useUsableNetScrolling';

export const UsableNetBanner = () => {
  const isEnabled = useFeatureFlag('cels-3985-usable-net-enabled');

  const { isUsableNetReady } = useUsableNet();
  const { isScrolled } = useUsableNetScrolling();
  const { formatMessage } = useIntl();

  const shouldRenderBanner = isEnabled && isUsableNetReady;

  // ─── Scroll Animation ──────────────────────────────────────────────────────

  const height = useDerivedValue(() => {
    return withTiming(isScrolled ? 0 : USABLE_NET_BANNER_HEIGHT, ANIMATION);
  });

  const animatedStyle = useAnimatedStyle(() => ({ height: height.value }));

  // ─── Adding Class Name ─────────────────────────────────────────────────────

  const ref = useRef<HTMLAnchorElement>(null);
  useWebClassName(ref, shouldRenderBanner ? USABLE_NET_CLASS_NAME : null);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const focusEnableAccessibilityLink = useCallback(() => {
    if (!shouldRenderBanner) return;

    ref.current?.focus?.();
  }, [shouldRenderBanner]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(focusEnableAccessibilityLink, [focusEnableAccessibilityLink]);

  // ───────────────────────────────────────────────────────────────────────────

  if (!shouldRenderBanner) return null;

  const bannerStyle = [styles.container, animatedStyle, !isScrolled && styles.bannerBorder];

  return (
    <Animated.View
      accessibilityRole="complementary"
      accessibilityLabel={formatMessage(messages.a11yLabel)}
      style={bannerStyle}
    >
      <BodyText style={[linkStyle, styles.link]} underline sizeMatch={['12']}>
        <A
          // @ts-expect-error TS2769: `A` on web renders an anchor, but its ref type is RN `Text`
          ref={ref}
          style={styles.link}
          href="#"
          tabIndex={0}
          onPress={enableAccessibility}
        >
          <FormattedMessage {...messages.enableAccessibility} />
        </A>
      </BodyText>
    </Animated.View>
  );
};

const enableAccessibility = () => {
  (window as unknown as UsableNetWindow)?.enableUsableNetAssistive?.();
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'UsableNet Assistive',
    description: 'UsableNet | Assistive Label',
  },
  enableAccessibility: {
    defaultMessage: 'Enable Accessibility',
    description: 'UsableNet | Enable Accessibility',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ANIMATION = { duration: 500, easing: Easing.out(Easing.exp) };
const BACKGROUND_COLOR = theme.colors.CREAM;
const USABLE_NET_CLASS_NAME = 'UsableNetAssistive';

// ─── Styles ──────────────────────────────────────────────────────────────────

const linkStyle: TextStyle = webOnlyStyles({ textUnderlineOffset: 3 });

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    backgroundColor: BACKGROUND_COLOR,
    paddingHorizontal: theme.spacing['3'],
  },
  bannerBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
  },
  link: {
    color: theme.colors.KALE,
    backgroundColor: BACKGROUND_COLOR, // NOTE: a11y vendor specifically requested background color on the pressable element
    borderRadius: theme.radius.medium,
    padding: theme.spacing['1'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UsableNetWindow = {
  enableUsableNetAssistive: () => void;
  disableUsableNetAssistive: () => void;
};
